import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  /** @type {number | null} */
  #rafId = null;

  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    // Cancel any pending frame before scheduling a new one
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;

      // All reads first — getBoundingClientRect inside a RAF reads from already-computed
      // layout and does not force a synchronous recalculation
      const rect = this.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // All writes after all reads — no interleaving to avoid layout thrash
      this.style.top = OFFSET + 'px';

      if (rect.right > viewportWidth) {
        this.style.left = `-${rect.right - viewportWidth + OFFSET}px`;
      } else if (rect.left < 0) {
        this.style.left = `${Math.abs(rect.left) + OFFSET}px`;
      }

      this.#mutationObserver.takeRecords();
    });
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
